import React, {useCallback, useEffect, useRef, useState} from 'react';
import {InputStatusModel} from "features/worksheet/process/job/models";
import {Link} from "react-router-dom";
import {
    MdOutlineCheckCircleOutline, MdPauseCircleOutline,
} from "react-icons/md";
import DefaultModal from "components/Modal/DefaultModal";
import JobChangeStatus from "features/worksheet/process/job/components/JobChangeStatus";
import {useAppDispatch, useAppSelector} from "helpers/redux";
import {RootState} from "app/store";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {getCancelList, getCancelWithFilterList, getList, getListWithFilter} from "../jobCheck.reducer";
import {customStatusOption, customWarnOption} from "helpers/alertLabel";
import {
    changeStatusJob,
    reset, waitingJob
} from "features/worksheet/process/job/job.reducer";
import {HttpClientError, RequestStatus} from "http-client";
import {DETAIL_TYPE_JOB_INPUT} from "../../../process/job/constants";
import AgDataGrid from "../../../../../components/DataGrid/AgDataGrid";
import {filterParams} from "../../../../../helpers/agTable";
import {rowNumberRenderer} from "../../../../../components/DataGrid/agGridCustomProps";
import {JOB_STATUS_PREP} from "../../../../../constants";
import {UserCredential} from "../../../../login/models";
import {checkRoleActionAccess} from "../../../../../routes/checkRoleAccess";
import {ActionEnum, MenuEnum} from "../../../../../routes/models";
import AgDataGridServerPaging from "../../../../../components/DataGrid/AgDataGridServerPaging";
import {isArrayEmpty} from "../../../../../helpers/checkEmptiness";
import {getSortModel, getSortType} from "../../../../approval/constants";
import {jobCancelSortOption, jobCheckSortOption} from "../constants";
import {dateSetTimeToNow} from "../../../../../helpers/convert";

const JobCancelTable = () => {
    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);
    const gridRef = useRef<any>(null);

    const user = useAppSelector(
        (state: RootState) => state.user.userAuth ?? {} as UserCredential
    );
    const jobCancelList = useAppSelector((state: RootState) => state.jobCheck.cancelList ?? []);
    const actionStatus = useAppSelector(
        (state: RootState) => state.job.actionStatus
    );
    const error = useAppSelector(
        (state: RootState) => state.job.error ?? ({} as HttpClientError)
    );

    const [showModal, setShowModal] = useState<boolean>(false);
    const [action, setAction] = useState<string>('');
    const [jobId, setJobId] = useState<string>('');
    const [tableName, setTableName] = useState('');

    // useEffect(() => {
    //     dispatch(getCancelList({}));
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);


    useEffect(() => {
        if (
            tableName === 'cancel' &&
            actionStatus !== undefined &&
            actionStatus !== RequestStatus.pending
        ) {
            MySwal.fire(
                customStatusOption(
                    actionStatus === RequestStatus.success,
                    error.data?.message,
                    'status has been changed!',
                    'failed to change status'
                )
            ).then(() => {
                dispatch(reset());
                // dispatch(getCancelList({}));
                refreshCache(undefined);
                dispatch(getList({}));
                setShowModal(false);
                setTableName('');
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableName, actionStatus, error]);

    const toggleModal = () => {
        setShowModal((prevState) => !prevState);
        setAction('');
        setJobId('');
    };

    const openModal = (action: string, id: string) => {
        setAction(action);
        setShowModal(true);
        setJobId(id);
    };

    const onStatusClicked = (id: string, action: string, data?: InputStatusModel) => {
        MySwal.fire(
            customWarnOption(
                'Waiting Job',
                'Are you sure you want to waiting this job? You will not be able to recover this later!',
            )
        ).then((result) => {
            if (result.isConfirmed) {
                setTableName('cancel');
                dispatch(
                    waitingJob({
                        job_id: id,
                        waiting_notes: data?.notes ?? '',
                        waiting_subject_id:
                            data?.pause_subject_id ?? '',
                        waiting_by_user: user.user_id
                    })
                );
            }
        });
    };

    const onActionClicked = (job_id: string) => {
        MySwal.fire(
            customWarnOption(
                'Active Job',
                'Are you sure to active this job? You will not be able to recover this later!',
            )
        ).then((result) => {
            if (result.isConfirmed) {
                setTableName('cancel');
                dispatch(changeStatusJob({
                    job_id: job_id,
                    status: JOB_STATUS_PREP,
                    userId: user.user_id
                }))
            }
        });
    };

    const ActionCellRender = (props: any) => {
        return (
            <div className="flex align-middle h-[100%]">
                {
                    checkRoleActionAccess(user?.roleName, MenuEnum.WorksheetCancelJob, ActionEnum.ActionCheck) &&
                    <button onClick={() => onActionClicked(props.data?.id)}>
                        <MdOutlineCheckCircleOutline
                            className="mr-2 text-2xl text-primary-color"
                        />
                    </button>
                }
                {
                    checkRoleActionAccess(user?.roleName, MenuEnum.WorksheetCancelJob, ActionEnum.ActionWaiting) &&
                    <button onClick={() => openModal('waiting', props.data?.id)}>
                        <MdPauseCircleOutline className="mr-2 text-2xl text-primary-color"/>
                    </button>
                }
            </div>
        )
    }

    const JobNumberLinkCellRender = (props: any) => (
        <Link
            to={`/worksheet/ocalc/job-detail/${props.data?.id}`}
            className="underline text-blue-700"
        >
            {props.data?.number_job}
        </Link>
    );

    const [columnDefs, _setColumnDefs] = useState([
        {
            headerName: '#',
            field: 'rowNumber',
            cellRenderer: rowNumberRenderer, // Use the custom cell renderer
            suppressSizeToFit: true,
            filter: false,
            sortable: false,
        },
        {
            field: 'sales_order', headerName: 'Sales Order', filter: false,
            sortable: false,
        },
        {
            field: 'number_id', headerName: 'ID Number', sortable: true,
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            headerName: 'Job Number',
            field: 'number_job',
            sortable: true,
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            },
            suppressCellFlash: true,
            cellRenderer: JobNumberLinkCellRender,
        },
        {
            field: 'project_name', headerName: 'Project', sortable: true,
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            field: 'department_owner_name', headerName: 'Department Owner', filter: false,
            sortable: false,
        },
        {
            field: 'cancel_by_user_name', headerName: 'Cancel By', filter: false,
            sortable: false,
        },
        {
            field: 'cancel_ts',
            headerName: 'Cancel Date',
            filter: false,
            sortable: false,
            cellDataType: 'date_time'
        },
        {
            field: 'kind_name', headerName: 'KoJ', sortable: true,
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            field: 'waiting_subject_name', headerName: 'Waiting Subject', filter: false,
            sortable: false,
        },
        {
            field: 'ts_created',
            headerName: 'Request Date',
            cellDataType: 'date',
            sortable: true,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: ['inRange'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'status_name', headerName: 'Status', sortable: true,
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {
            field: 'type_name', headerName: 'Type', filter: false,
            sortable: false,
        },
        {
            field: 'district_name', headerName: 'District', sortable: true,
            filter: 'agTextColumnFilter', filterParams: {
                filterOptions: ['contains'],
                maxNumConditions: 1,
            }
        },
        {field: 'po_number', headerName: 'PO Number', filter: false, sortable: false},
        {
            field: 'date_due',
            headerName: 'Due Date',
            cellDataType: 'date',
            sortable: true,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: ['inRange'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'pole_total', headerName: 'Req Pole', filter: false,
            sortable: false,
        },
        {
            headerName: 'Actions',
            field: 'actions',
            pinned: 'right',
            suppressCellFlash: true,
            minWidth: 220,
            cellRenderer: ActionCellRender,
            cellStyle: () => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }),
        },
    ]);

    const getServerSideDataSource = () => {
        return {
            getRows: (params: any) => {
                const sortModel = isArrayEmpty(params.request.sortModel) ? undefined : getSortModel(jobCancelSortOption, params.request.sortModel[0].colId) ?? undefined;
                const sortType = isArrayEmpty(params.request.sortModel) ? undefined : getSortType(params.request.sortModel[0].sort) ?? undefined;
                const {dateFrom: startDate, dateTo: endDate} = params.request.filterModel?.ts_created || {};
                const {dateFrom: startDueDate, dateTo: endDueDate} = params.request.filterModel?.date_due || {};

                dispatch(
                    getCancelWithFilterList({
                        number_id: params.request.filterModel?.number_id?.filter,
                        number_job: params.request.filterModel?.number_job?.filter,
                        other_number: params.request.filterModel?.other_number?.filter,
                        koj: params.request.filterModel?.kind_name?.filter,
                        project: params.request.filterModel?.project_name?.filter,
                        status: params.request.filterModel?.status_name?.filter,
                        district: params.request.filterModel?.district_name?.filter,
                        startReqDate: startDate !== undefined ? dateSetTimeToNow(startDate) : undefined,
                        endReqDate: endDate !== undefined ? dateSetTimeToNow(endDate) : undefined,
                        startDueDate: startDueDate !== undefined ? dateSetTimeToNow(startDueDate) : undefined,
                        endDueDate: endDueDate !== undefined ? dateSetTimeToNow(endDueDate) : undefined,
                        column: sortModel !== undefined ? parseInt(sortModel ?? "0") : undefined,
                        sort: sortModel !== undefined ? sortType : undefined,
                        skip: params.request.startRow,
                        take: params.api.paginationGetPageSize()
                    })
                ).then((res: any) => {
                    params.success({
                        rowData: res.payload?.data,
                        rowCount: res.payload.rows,
                    });
                }).catch((_err: any) => {
                    params.fail();
                })
            },
        };
    }

    const refreshCache = useCallback((route: any) => {
        const purge = !!document?.querySelector("#purge" as any)?.checked;
        gridRef.current.api.refreshServerSide({route: route, purge: purge});
    }, []);

    return (
        <div>
            <div className="main-data-grid mt-5">
                {/*<AgDataGrid rowData={jobCancelList} columnDefs={columnDefs} supportSaveTableState tableStateName={'job-data-cancel-job'} defaultPageSize={20}/>*/}
                <AgDataGridServerPaging ref={gridRef} columnDefs={columnDefs}
                                        getServerSideDataSource={getServerSideDataSource} supportSaveTableState
                                        tableStateName={'job-data-cancel-job'} defaultPageSize={20}/>
                <DefaultModal
                    title="Change Status"
                    show={showModal}
                    onClose={toggleModal}
                    body={<JobChangeStatus action={action} jobId={jobId} onSubmit={onStatusClicked}/>}
                />
            </div>
        </div>
    );
};

export default JobCancelTable;
